<template lang="pug">
    layout.contentTransparent
        div.layoutWrapper
            v-layout(row wrap)
                v-flex(xs12)
                    div.sessionHeader(:class="{ withLogo: (isMember && avatar) }")

                        // Title
                        div.titleWrapper
                            h1(v-html="titleText ? titleText : $t('sessions.list.title')")
                v-progress-linear.preloader(
                    v-if="isLoading"
                    indeterminate
                    color="info"
                )

        template
            v-layout(row wrap justify-space-between)
                v-flex(xs12 sm4 md4 lg3)
                    div.c-form

                        // Search field
                        v-text-field.search-input(
                            :append-icon="search ? 'close' : 'search'"
                            @click:append="search = ''"
                            outline
                            v-model="search"
                            :label="$t('fields.search')"
                        )

                v-flex(xs12 sm8 md8)
                    div.createButtonWrapper

                        // Participate in Challenge
                        div.c-form.mr-2.pt-1
                            v-checkbox.mt-0(
                                v-if="isAdmin"
                                color="action"
                                v-model="isSuggested"
                                :label="$t('fields.isSuggested')"
                                @change="resetPagination"
                            )

                        // Add recorded Meditation button
                        button-component.mx-0.my-0.mr-1(
                            color="action"
                            large
                            v-if="isAdmin"
                            :action="addSession"
                        ) {{ $t('sessions.list.createRecordedButton') }}

                        // Create Meditation button
                        button-component.mx-0.my-0(
                            color="action"
                            large
                            v-if="isAdmin"
                            :action="createSession"
                        ) {{ $t('sessions.list.createButton') }}

            // Horizontal line
            separator-component.overlay

            div.filters
                div.mr-3

                    // Theme filter
                    filter-component(
                        name="Theme"
                        filterName="theme"
                        :items="$t('selection.themes')"
                        :value="filters.theme"
                        :callbackFunction="filtersCallback"
                    )

                div.mr-3(v-if="filters.theme === 'live'")

                    // Status filter
                    filter-component(
                        name="Status"
                        filterName="type"
                        :items="types"
                        :callbackFunction="filtersCallback"
                    )

                div.mr-3(v-if="isAdmin")

                    // Business Name filter
                    filter-component(
                        name="Business Name"
                        filterName="businessId"
                        :items="businesses"
                        :callbackFunction="filtersCallback"
                    )

                div.mr-3
                    // Duration filter
                    filter-component(
                        name="Duration"
                        filterName="duration"
                        :value="predefinedDuration"
                        :items="$t('selection.sortDuration')"
                        :callbackFunction="sortingCallback"
                    )

                div.mr-3
                    // Instructor filter
                    filter-component.mr-4(
                        v-if="!isInstructor"
                        name="Instructor"
                        filterName="instructor"
                        :items="$t('selection.instructors')"
                        :callbackFunction="instructorFiltersCallback"
                    )

                div.mr-3
                    v-btn.clearAllFilters.ml-0(
                        v-if="filters && Object.keys(filters).length > 1"
                        dark
                        small
                        icon
                        @click="clearAllFilters"
                    )
                        v-icon(size="18") refresh


            v-data-iterator(
                item-key="id"
                content-tag="v-layout"
                no-data-text="No videos available"
                row
                wrap
                :items="items"
                :rows-per-page-items="[2]"
                :pagination.sync="pagination"
                :total-items="total"
            )
                template(v-slot:item="props")
                    v-flex(xs12 sm6 md4 lg3)
                        meditation-component(:item="props.item" :duration="meditationDuration" :key="props.item.id")

        div.bottomSpacer
</template>

<script>
    import Layout from '../../Layout/Layout';
    import i18n from '~/i18n';
    import sessions from '~api/sessions';
    import Button from '~components/Button';
    import ButtonIcon from '~components/ButtonIcon';
    import Filter from '~components/Filter';
    import Separator from '~components/Separator';
    import Status from '~components/Status';
    import Title from '~components/Title';
    import { mapActions, mapGetters } from 'vuex';
    import formattedDate from '~filters/FormattedDate';
    import formattedDateTime from '~filters/FormattedDateTime';
    import formattedFullName from '~filters/FormattedFullName';
    import formattedTime from '~filters/FormattedTime';
    import VueTypes from 'vue-types';

    export default {
        components: {
            Layout,
            MeditationComponent: () =>
                import('../../../../components/Meditation'),
            TitleComponent: Title,
            ButtonComponent: Button,
            ButtonIconComponent: ButtonIcon,
            StatusComponent: Status,
            FilterComponent: Filter,
            SeparatorComponent: Separator
        },
        filters: {
            formattedDate,
            formattedTime,
            formattedFullName,
            formattedDateTime
        },
        props: {
            theme: VueTypes.string.def(''),
            title: VueTypes.string.def(''),
            type: VueTypes.string.def(''),
            duration: VueTypes.number.def(0)
        },
        data() {
            return {
                expand: false,
                headers: this.$t('sessions.tableHeaders'),
                titleText: this.title,
                filters: {
                    type: this.type,
                    theme: this.theme
                },
                items: [],
                meditationDuration: this.duration,
                pagination: {},
                sortBy: null,
                orderBy: null,
                total: 0,
                rowsPerPage: [10],
                search: null,
                timeout: null,
                routeInitialized: false,
                isLoading: false,
                menuStartedAt: false,
                menuFinishedAt: false,
                predefinedDuration: null,
                instructorFullName: '',
                isSuggested: false
            };
        },
        mounted() {
            // Subscribe on meditation status change
            this.sockets.subscribe('general', () => {
                this.resetPagination();
            });
        },
        beforeDestroy() {
            // Unsubscribe from listening meditation status change
            this.sockets.unsubscribe('general');
        },
        computed: {
            /**
             * Types list for status filter.
             * @returns {(object|Array)}
             */
            types() {
                let types = this.headers.find(item => item.value === 'type');

                types = types.filter || [];

                // If role not equals admin remove archived type
                if (!this.isAdmin) {
                    types = types.filter(elem => elem.value !== 'archived');
                }

                return types;
            },
            /**
             * Businesses list for business filter.
             * @returns {(object|Array)}
             */
            businesses() {
                if (this.isMember) return null;
                const businesses = this.$store.getters['sessions/businesses'];

                // Modify array for filter by business name
                businesses.forEach(el => {
                    el.title = el.text;
                    return el;
                });

                // Add filter by all businesses
                businesses.unshift({
                    title: 'All',
                    value: null
                });

                return businesses;
            },

            ...mapGetters('auth', [
                'isAdmin',
                'isInstructor',
                'isMember',
                'avatar'
            ])
        },

        /**
         * Retrieve managers
         */
        async beforeRouteEnter(to, from, next) {
            // TODO: Create mixin and refactor
            // Fetch data from API.
            let params = {};

            const { theme, title, ...rest } = to.query;

            params = { ...rest };

            if (theme && theme !== '10mins') {
                params.theme = to.query.theme;
            }

            const { data } = await sessions.list(params);

            next(vm => {
                // Start progress bar.
                vm.isLoading = true;

                let paginationBlock = document.querySelector(
                    '.v-data-iterator__actions__range-controls'
                );

                if (title) vm.titleText = title;

                if (data.items && data.meta) {
                    vm.items = data.items;
                    vm.total = data.meta.total;
                    vm.pagination = {
                        page: data.meta.page,
                        rowsPerPage: data.meta.limit
                    };

                    if (params.theme) {
                        i18n.t('selection.themes').some(item => {
                            if (item.value === params.theme) {
                                vm.titleText = item.title;
                                return true;
                            }

                            return false;
                        });

                        vm.filters.theme = params.theme;
                    }

                    if (params.duration) {
                        vm.titleText = 'Mini Meditations';
                        vm.filters.duration = params.duration;
                    }

                    if (params.type) vm.filters.type = params.type;

                    if (params.sortBy && params.orderBy) {
                        vm.sortBy = params.sortBy;
                        vm.orderBy = params.orderBy;
                        vm.predefinedDuration = params.orderBy;
                    }

                    if (params.instructorId) {
                        const instructorFullName = i18n
                            .t('selection.instructors')
                            .find(
                                instructor =>
                                    instructor.value === params.instructorId
                            ).title;
                        vm.filters.instructorId = params.instructorId;
                        vm.titleText = `Meditation by ${instructorFullName}`;
                    }

                    if (paginationBlock) {
                        if (vm.total <= vm.pagination.rowsPerPage) {
                            paginationBlock.style.display = 'none';
                        } else {
                            paginationBlock.style.display = '';
                        }
                    }
                }

                vm.$nextTick(() => {
                    vm.routeInitialized = true;
                    // Stop progress bar.
                    vm.isLoading = false;
                });
            });
        },

        watch: {
            /**
             * Watch search and set pagination param on change.
             */
            search(value) {
                clearTimeout(this.timeout);

                this.timeout = setTimeout(() => {
                    this.pagination = {
                        ...this.pagination,
                        search: value
                    };
                }, 500);
            },

            /**
             * Watch pagination and update managers data on change.
             */
            pagination: {
                // TODO: create mixin and refactor
                async handler(paginationData) {
                    let paginationBlock = document.querySelector(
                        '.v-data-iterator__actions__range-controls'
                    );

                    if (!this.routeInitialized) {
                        return;
                    }

                    // Start progress bar.
                    this.isLoading = true;

                    // Collect params.
                    const params = {
                        page: paginationData.page,
                        sortBy: this.sortBy,
                        orderBy: this.orderBy
                    };

                    if (
                        paginationData.search &&
                        paginationData.search.length > 0
                    ) {
                        params.search = paginationData.search;
                    }

                    if (this.filters.type) {
                        params.type = this.filters.type;
                    }

                    if (this.filters.instructorId) {
                        params.instructorId = this.filters.instructorId;
                    }

                    if (
                        this.filters.duration &&
                        this.filters.theme === '10mins'
                    ) {
                        params.duration = this.filters.duration;
                    }

                    if (this.filters.businessId) {
                        params.businessId = this.filters.businessId;
                    }

                    if (this.filters.theme && this.filters.theme.length > 0) {
                        this.$t('selection.themes').map(item => {
                            if (item.value === this.filters.theme) {
                                if (this.filters.instructorId) {
                                    this.titleText = `Meditation by ${this
                                        .instructor.fullName ||
                                        this.instructorFullName}, theme ${
                                        item.title
                                    }`;
                                } else {
                                    this.titleText = item.title;
                                }
                            }
                        });

                        if (this.filters.theme === '10mins') {
                            params.duration = 659;
                            this.meditationDuration = 659;
                        } else {
                            params.theme = this.filters.theme;
                            this.filters.duration = 0;
                        }
                    } else {
                        if (!this.filters.instructorId) this.titleText = null;
                    }

                    if (this.isSuggested) {
                        params.isSuggested = this.isSuggested;
                    }

                    this.updateQueryParam(params);

                    // Fetch data from API.
                    const { data } = await sessions.list(params);

                    this.items = data.items;

                    this.total = data.meta.total;

                    if (paginationBlock) {
                        if (this.total <= this.pagination.rowsPerPage) {
                            paginationBlock.style.display = 'none';
                        } else {
                            paginationBlock.style.display = '';
                        }
                    }

                    // Stop progress bar.
                    this.isLoading = false;

                    this.updateQueryParam();
                },
                deep: true
            }
        },

        beforeMount() {
            if (this.isAdmin || this.isInstructor) {
                // Get businesses for selection
                this.readBusinesses();
            }

            // If role not equals admin hide column business name
            if (!this.isAdmin) {
                this.headers = this.headers.filter(function(obj) {
                    return obj.value !== 'businessId';
                });
            }
        },

        async beforeRouteUpdate(to, from, next) {
            if (to.fullPath === from.fullPath) {
                next(false);
            } else {
                next();
            }
        },

        methods: {
            ...mapActions('sessions', ['readBusinesses']),

            /**
             * Update url based on filter values
             */
            updateQueryParam(params) {
                if (!params) return;

                const availableParams = {};
                for (const key in params) {
                    if (params[key]) availableParams[key] = params[key];
                }
                this.$router.replace({
                    query: {
                        ...this.$route.query,
                        ...availableParams
                    }
                });
            },

            /**
             * If isset filter with endedAt date disabled all bigger dates.
             * @param {string} val
             * @returns {boolean}
             */
            allowedStartDates: function(val) {
                if (this.filters && this.filters.finishedAt) {
                    return val < this.filters.finishedAt;
                }

                return true;
            },

            /**
             * If isset filter with startedAt date disabled all less dates.
             * @param {string} val
             * @returns {boolean}
             */
            allowedEndDates: function(val) {
                if (this.filters && this.filters.startedAt) {
                    return val > this.filters.startedAt;
                }

                return true;
            },

            /**
             * Reset pagination.
             */
            resetPagination() {
                this.pagination = { ...this.pagination };
            },

            /**
             * Redirect to Create Meditation Page.
             */
            addSession() {
                this.$router.push({
                    name: 'dashboard.sessions.createRecorded'
                });
            },
            /**
             * Redirect to Create Meditation Page.
             */
            createSession() {
                this.$router.push({ name: 'dashboard.sessions.create' });
            },

            /**
             * Get filter param on change filter.
             * @param {object} e
             */
            filtersCallback(e) {
                this.filters[e.name] = e.value;
                this.resetPagination();
            },
            /**
             * Clear filter param on change filter.
             */
            clearAllFilters() {
                this.filters = {
                    type: this.type
                };
                this.titleText = `Meditations`;
                this.sortBy = null;
                this.orderBy = null;
                this.$router.replace({
                    query: {}
                });
                this.resetPagination();
            },

            /**
             * Get  insructor param on change filter.
             * @param {object} e
             */
            instructorFiltersCallback(e) {
                if (!e.value) return;
                this.filters['instructorId'] = e.value;
                this.titleText = `Meditation by ${e.title}`;
                this.instructorFullName = e.title;
                this.resetPagination();
            },

            /**
             * Get sort params on change filter.
             * @param {object} e
             */
            sortingCallback(e) {
                if (e.value) {
                    this.sortBy = e.name;
                    this.orderBy = e.value;
                } else {
                    this.sortBy = null;
                    this.orderBy = null;
                }

                this.resetPagination();
            },

            /**
             * Change sort items.
             * @param {string} column
             */
            changeSort(column) {
                if (this.pagination.sortBy === column) {
                    this.pagination.descending = !this.pagination.descending;
                } else {
                    this.pagination.sortBy = column;
                    this.pagination.descending = false;
                }
            }
        }
    };
</script>

<style lang="stylus" scoped>
    @import '../../../../assets/style/_variables.styl'
    @import "../../../../assets/style/_mixins.styl"

    .createButtonWrapper
        display: flex
        justify-content: flex-end

    .text-left
        text-align: left

    .instructorName
        padding-bottom: 10px
        font-size: 12px

    .description
        position: relative

        &:before
            position: absolute
            top: 8px
            left: 105px
            content: ""
            width: 75%
            height: 1px
            background: linear-gradient(90deg, rgba(255, 255, 255, 0.35) 70%, #022341 100%)

        &:after
            position: absolute
            top: 8px
            left: 0
            content: ""
            width: 20px
            height: 1px
            background: $theme.secondary

    .title
        margin-left: 20px
        padding: 5px 10px
        background-color: $theme.primary
        font-size: 12px !important
        color: $theme.secondary

    >>> .dateHeader
        min-width: 180px
        max-width: 200px

    >>> .SessionActions
        min-width: 210px

    >>> .date-filter-input
        .v-label
        .v-label.v-label--active
            color: $theme.white

        .v-input__icon .v-icon
            color: $theme.white

        &.v-text-field > .v-input__control > .v-input__slot:after
            border-style: none

    .MeditationName
        display: flex
        padding-top: 10px

    .linkAction
        color: $theme.primary
        font-size: 14px

    .filters
        display: flex
        align-items: center
        margin-bottom: 30px

    .sessionHeader
        display: flex
        align-items: center
        justify-content: space-between
        flex-direction: row
        margin: 18px 0 30px
        position: relative

        &.withLogo
            margin: 8px 0 24px

        .titleWrapper h1
            padding-bottom: 0
            margin-bottom: 0
            font-size: remify(40px)
            color: $theme.white
            font-weight: 300

    .businessImageWrapper
        display: flex

    .preloader
        position: absolute
        right: 0
        top: -16px

    .businessImage
        border-radius: 10px
        overflow: hidden

        img
            width: 65px
            height: 65px

    .clearAllFilters
        opacity: 0.7
        border-radius: 5px

        span
            font-size: 14px

        i
            font-size: 16px

        span
            text-transform: capitalize

    .bottomSpacer
        height: 90px

    @media screen and (max-width: $grid-breakpoints.sm - 1)
        .sessionHeader
            margin: 24px 0 12px

            .titleWrapper h1
                font-size: remify(20px)
                margin-bottom: 0

        // Kill Vuetify default input vertical spacing
        >>> .search-input.v-text-field
            margin-top: 0
            margin-bottom: 0
            padding-top: 0

        // Pull separator up
        .overlay
            margin-top: 0 !important

        .overlay >>> .separator
            margin: 4px 0 8px

        .filters
            flex-wrap: nowrap
            margin-bottom: 12px
            align-items: center

            > div
                width: auto
                margin-bottom: 0
                margin-right: 12px !important

        .clearAllFilters
            span
                font-size: 12px
        .createButtonWrapper
            flex-direction: column

            .btn
                margin-bottom: 10px !important
                width: 100%
</style>
